// React
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import moment from "moment";

// MUI
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

const PREFIX = "Additionals";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
    switch: `${PREFIX}-switch`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
    [`& .${classes.switch}`]: {
        "& .MuiSwitch-switchBase .MuiSwitch-thumb": {
            color: "#FFF",
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: "#031851",
        },
        "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
            color: "#FF5968",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#FFA8B1",
        },
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

export const Additionals = (props) => {
    // Properties
    const {
        clientMarket,
        isError,
        set,
        setalertType,
        setShow,
        setTexto,
        sumTotal,
        updateProject,
        values,
        showConfirm,
        sumTotalAddHours,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component functions */
    const handleChange = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
        });
    };

    const handleChangeHoursAcc = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
            projectFee: event.target.value * values.addHourFee,
            projectTotal: event.target.value * values.addHourFee * (1 - values.projectDiscount / 100),
        });
    };

    const handleChangeCheck = (event, value) => {
        switch (value) {
            case 0:
                if (values.checkedAROG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedAR",
                        toth: "tothAR",
                        startDate: "startDateAR",
                        finishDate: "finishDateAR",
                    });
                } else {
                    set({
                        ...values,
                        checkedAR: event.target.checked,
                        tothAR: 0,
                        startDateAR: event.target.checked ? values.initDate : null,
                        finishDateAR: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 1:
                if (values.checkedAPOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedAP",
                        toth: "tothAP",
                        startDate: "startDateAP",
                        finishDate: "finishDateAP",
                    });
                } else {
                    set({
                        ...values,
                        checkedAP: event.target.checked,
                        tothAP: 0,
                        startDateAP: event.target.checked ? values.initDate : null,
                        finishDateAP: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 2:
                if (values.checkedClassOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedClass",
                        toth: "tothClass",
                        startDate: "startDateClass",
                        finishDate: "finishDateClass",
                    });
                } else {
                    set({
                        ...values,
                        checkedClass: event.target.checked,
                        tothClass: 0,
                        startDateClass: event.target.checked ? values.initDate : null,
                        finishDateClass: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 3:
                if (values.checkedMultiOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedMulti",
                        toth: "tothMulti",
                        startDate: "startDateMulti",
                        finishDate: "finishDateMulti",
                    });
                } else {
                    set({
                        ...values,
                        checkedMulti: event.target.checked,
                        tothMulti: 0,
                        startDateMulti: event.target.checked ? values.initDate : null,
                        finishDateMulti: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 4:
                if (values.checkedPerOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedPer",
                        toth: "tothPer",
                        startDate: "startDatePer",
                        finishDate: "finishDatePer",
                    });
                } else {
                    set({
                        ...values,
                        checkedPer: event.target.checked,
                        tothPer: 0,
                        startDatePer: event.target.checked ? values.initDate : null,
                        finishDatePer: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 5:
                if (values.checkedSalesOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedSales",
                        toth: "tothSales",
                        startDate: "startDateSales",
                        finishDate: "finishDateSales",
                    });
                } else {
                    set({
                        ...values,
                        checkedSales: event.target.checked,
                        tothSales: 0,
                        startDateSales: event.target.checked ? values.initDate : null,
                        finishDateSales: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 6:
                if (values.checkedChecksOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedChecks",
                        toth: "tothChecks",
                        startDate: "startDateChecks",
                        finishDate: "finishDateChecks",
                    });
                } else {
                    set({
                        ...values,
                        checkedChecks: event.target.checked,
                        tothChecks: 0,
                        startDateChecks: event.target.checked ? values.initDate : null,
                        finishDateChecks: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 7:
                if (values.checkedOrdOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedOrd",
                        toth: "tothOrd",
                        startDate: "startDateOrd",
                        finishDate: "finishDateOrd",
                    });
                } else {
                    set({
                        ...values,
                        checkedOrd: event.target.checked,
                        tothOrd: event.target.checked ? 1.5 : 0,
                        startDateOrd: event.target.checked ? values.initDate : null,
                        finishDateOrd: event.target.checked ? values.endDate : null,
                        startDateEsp: !event.target.checked && values.checkedEsp ? values.initDate : null,
                        finishDateEsp: !event.target.checked && values.checkedEsp ? values.endDate : null,
                    });
                }
                break;
            case 8:
                if (values.checkedEspOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedEsp",
                        toth: "tothEsp",
                        startDate: "startDateEsp",
                        finishDate: "finishDateEsp",
                    });
                } else {
                    set({
                        ...values,
                        checkedEsp: event.target.checked,
                        tothEsp: event.target.checked ? 5 : 0,
                        startDateEsp: event.target.checked
                            ? values.checkedOrd && values.finishDateOrd
                                ? moment(values.finishDateOrd).add(1, "months").startOf("month")
                                : values.initDate
                            : null,
                        finishDateEsp: event.target.checked
                            ? values.checkedOrd && values.finishDateOrd
                                ? null
                                : values.endDate
                            : null,
                    });
                }
                break;
            case 9:
                if (values.checkedMuniOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedMuni",
                        toth: "tothMuni",
                        startDate: "startDateMuni",
                        finishDate: "finishDateMuni",
                    });
                } else {
                    set({
                        ...values,
                        checkedMuni: event.target.checked,
                        tothMuni: 0,
                        startDateMuni: event.target.checked ? values.initDate : null,
                        finishDateMuni: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 10:
                if (values.checkedOthersOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedOthers",
                        nameOthers: "nameOthers",
                        toth: "tothOthers",
                        startDate: "startDateOthers",
                        finishDate: "finishDateOthers",
                    });
                } else {
                    set({
                        ...values,
                        checkedOthers: event.target.checked,
                        nameOthers: "",
                        tothOthers: 0,
                        startDateOthers: event.target.checked ? values.initDate : null,
                        finishDateOthers: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            default:
                set({
                    ...values,
                    projectFee: 0,
                    projectTotal: 0,
                    checkedAcc: event.target.checked,
                    tothAcc: 0,
                    startDateAcc: event.target.checked ? values.initDate : null,
                    finishDateAcc: event.target.checked ? values.endDate : null,
                });
                break;
        }
    };

    const handleChangeStartDateAdd = (date, val) => {
        let startDate = moment(date).startOf("month");
        switch (val) {
            case 0:
                if (!date || !values.finishDateAR) {
                    set({
                        ...values,
                        startDateAR: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAR))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAR: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.finishDateAP) {
                    set({
                        ...values,
                        startDateAP: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAP))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAP: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.finishDateClass) {
                    set({
                        ...values,
                        startDateClass: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateClass))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateClass: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.finishDateMulti) {
                    set({
                        ...values,
                        startDateMulti: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateMulti))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateMulti: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.finishDatePer) {
                    set({
                        ...values,
                        startDatePer: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDatePer))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDatePer: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.finishDateSales) {
                    set({
                        ...values,
                        startDateSales: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateSales))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateSales: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.finishDateChecks) {
                    set({
                        ...values,
                        startDateChecks: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateChecks))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateChecks: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.finishDateOrd) {
                    set({
                        ...values,
                        startDateOrd: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOrd))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOrd: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 8:
                if (!date || !values.finishDateEsp) {
                    set({
                        ...values,
                        startDateEsp: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateEsp))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateEsp: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 9:
                if (!date || !values.finishDateMuni) {
                    set({
                        ...values,
                        startDateMuni: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateMuni))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateMuni: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.finishDateOthers) {
                    set({
                        ...values,
                        startDateOthers: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOthers))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOthers: moment(date).startOf("month"),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.finishDateAcc) {
                    set({
                        ...values,
                        startDateAcc: date ? moment(date).startOf("month") : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAcc))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAcc: moment(date).startOf("month"),
                        });
                    }
                }
                break;
        }
    };

    const handleChangeEndDateAdd = (date, val) => {
        let endDate = moment(date).endOf("month").startOf("day");
        switch (val) {
            case 0:
                if (!date || !values.startDateAR) {
                    set({
                        ...values,
                        finishDateAR: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAR))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAR: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.startDateAP) {
                    set({
                        ...values,
                        finishDateAP: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAP))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAP: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.startDateClass) {
                    set({
                        ...values,
                        finishDateClass: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateClass))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateClass: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.startDateMulti) {
                    set({
                        ...values,
                        finishDateMulti: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateMulti))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateMulti: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.startDatePer) {
                    set({
                        ...values,
                        finishDatePer: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDatePer))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDatePer: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.startDateSales) {
                    set({
                        ...values,
                        finishDateSales: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateSales))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateSales: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.startDateChecks) {
                    set({
                        ...values,
                        finishDateChecks: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateChecks))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateChecks: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.startDateOrd) {
                    set({
                        ...values,
                        finishDateOrd: date ? moment(date).endOf("month") : date,
                        checkedEsp: false,
                        tothEsp: 0,
                        startDateEsp: null,
                        finishDateEsp: null,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOrd))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOrd: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 8:
                if (!date || !values.startDateEsp) {
                    set({
                        ...values,
                        finishDateEsp: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateEsp))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateEsp: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 9:
                if (!date || !values.startDateMuni) {
                    set({
                        ...values,
                        finishDateMuni: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateMuni))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateMuni: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.startDateOthers) {
                    set({
                        ...values,
                        finishDateOthers: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOthers))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOthers: moment(date).endOf("month"),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.startDateAcc) {
                    set({
                        ...values,
                        finishDateAcc: date ? moment(date).endOf("month") : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAcc))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAcc: moment(date).endOf("month"),
                        });
                    }
                }
                break;
        }
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader title={t("services.additionals")} sx={{ color: "#ff5968" }} />
                    <Divider />
                    <CardContent sx={{ pb: 5 }}>
                        <Grid container spacing={2} alignItems="center">
                            {values.additionalsOnly && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedAcc"
                                                    checked={values.checkedAcc}
                                                    onChange={() => {
                                                        return false;
                                                    }}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                />
                                            }
                                            label="Accountant"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedAcc}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothAcc"
                                            onChange={handleChangeHoursAcc}
                                            value={values.tothAcc}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedAcc && isError && values.tothAcc === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedAcc}
                                            value={values.startDateAcc}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedAcc}
                                                    {...params}
                                                    error={values.checkedAcc && isError && !values.startDateAcc}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedAcc}
                                            value={values.finishDateAcc}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedAcc}
                                                    {...params}
                                                    error={values.checkedAcc && isError && !values.finishDateAcc}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            {clientMarket === "Estados Unidos" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedChecks"
                                                    checked={values.checkedChecks}
                                                    onChange={(value) => handleChangeCheck(value, 6)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={!values.initDate}
                                                />
                                            }
                                            label="Checks"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedChecks}
                                            label={t("miscellaneous.hours")}
                                            name="tothChecks"
                                            onChange={handleChange}
                                            value={values.tothChecks}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedSales && isError && values.tothChecks === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedChecks}
                                            value={values.startDateChecks}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 6)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedChecks}
                                                    {...params}
                                                    error={values.checkedChecks && isError && !values.startDateChecks}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedChecks}
                                            value={values.finishDateChecks}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 6)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedChecks}
                                                    {...params}
                                                    error={values.checkedChecks && isError && !values.finishDateChecks}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedAR"
                                            checked={values.checkedAR}
                                            onChange={(value) => handleChangeCheck(value, 0)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    }
                                    label="A/R"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedAR}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothAR"
                                    onChange={handleChange}
                                    value={values.tothAR}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedAR && isError && values.tothAR === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAR}
                                    value={values.startDateAR}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.startDateAR}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAR}
                                    value={values.finishDateAR}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.finishDateAR}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedAP"
                                            checked={values.checkedAP}
                                            onChange={(value) => handleChangeCheck(value, 1)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    }
                                    label="A/P"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedAP}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothAP"
                                    onChange={handleChange}
                                    value={values.tothAP}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedAP && isError && values.tothAP === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAP}
                                    value={values.startDateAP}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.startDateAP}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedAP}
                                    value={values.finishDateAP}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.finishDateAP}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedClass"
                                            checked={values.checkedClass}
                                            onChange={(value) => handleChangeCheck(value, 2)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    }
                                    label={t("services.classesCategories")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedClass}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothClass"
                                    onChange={handleChange}
                                    value={values.tothClass}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedClass && isError && values.tothClass === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedClass}
                                    value={values.startDateClass}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.startDateClass}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedClass}
                                    value={values.finishDateClass}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.finishDateClass}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedMulti"
                                            checked={values.checkedMulti}
                                            onChange={(value) => handleChangeCheck(value, 3)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    }
                                    label={t("services.multiEntity")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedMulti}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothMulti"
                                    onChange={handleChange}
                                    value={values.tothMulti}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedMulti && isError && values.tothMulti === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedMulti}
                                    value={values.startDateMulti}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 3)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.startDateMulti}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedMulti}
                                    value={values.finishDateMulti}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 3)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.finishDateMulti}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedPer"
                                            checked={values.checkedPer}
                                            onChange={(value) => handleChangeCheck(value, 4)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    }
                                    label={t("services.personalizedReporting")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedPer}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothPer"
                                    onChange={handleChange}
                                    value={values.tothPer}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedPer && isError && values.tothPer === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedPer}
                                    value={values.startDatePer}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 4)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.startDatePer}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedPer}
                                    value={values.finishDatePer}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 4)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.finishDatePer}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            {clientMarket === "Estados Unidos" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedSales"
                                                    checked={values.checkedSales}
                                                    onChange={(value) => handleChangeCheck(value, 5)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={!values.initDate}
                                                />
                                            }
                                            label={t("services.salesTaxes")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedSales}
                                            label={t("miscellaneous.hours")}
                                            name="tothSales"
                                            onChange={handleChange}
                                            value={values.tothSales}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedSales && isError && values.tothSales === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedSales}
                                            value={values.startDateSales}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 5)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSales}
                                                    {...params}
                                                    error={values.checkedSales && isError && !values.startDateSales}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedSales}
                                            value={values.finishDateSales}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 5)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSales}
                                                    {...params}
                                                    error={values.checkedSales && isError && !values.finishDateSales}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            {clientMarket === "Venezuela" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedOrd"
                                                    checked={values.checkedOrd}
                                                    onChange={(value) => handleChangeCheck(value, 7)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={values.checkedEsp || !values.initDate}
                                                />
                                            }
                                            label={t("proposals.addOrdTax")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedOrd}
                                            label={t("miscellaneous.hours")}
                                            name="tothOrd"
                                            onChange={handleChange}
                                            value={values.tothOrd}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 1.5,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedOrd && isError && values.tothOrd === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedOrd}
                                            value={values.startDateOrd}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 7)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedOrd}
                                                    {...params}
                                                    error={values.checkedOrd && isError && !values.startDateOrd}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedOrd}
                                            value={values.finishDateOrd}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 7)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedOrd}
                                                    {...params}
                                                    error={values.checkedOrd && isError && !values.finishDateOrd}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedEsp"
                                                    checked={values.checkedEsp}
                                                    onChange={(value) => handleChangeCheck(value, 8)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={values.checkedOrd || !values.initDate}
                                                />
                                            }
                                            label={t("proposals.addSpecial")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedEsp}
                                            label={t("miscellaneous.hours")}
                                            name="tothEsp"
                                            onChange={handleChange}
                                            value={values.tothEsp}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 3.5,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedEsp && isError && values.tothEsp === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedEsp}
                                            value={values.startDateEsp}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 8)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedEsp}
                                                    {...params}
                                                    error={values.checkedEsp && isError && !values.startDateEsp}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedEsp}
                                            value={values.finishDateEsp}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 8)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedEsp}
                                                    {...params}
                                                    error={values.checkedEsp && isError && !values.finishDateEsp}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedMuni"
                                                    checked={values.checkedMuni}
                                                    onChange={(value) => handleChangeCheck(value, 9)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={!values.initDate}
                                                />
                                            }
                                            label={t("proposals.addImpMun")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedMuni}
                                            label={t("miscellaneous.hours")}
                                            name="tothMuni"
                                            onChange={handleChange}
                                            value={values.tothMuni}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedMuni && isError && values.tothMuni === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedMuni}
                                            value={values.startDateMuni}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 9)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedMuni}
                                                    {...params}
                                                    error={values.checkedMuni && isError && !values.startDateMuni}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={!values.checkedMuni}
                                            value={values.finishDateMuni}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 9)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedMuni}
                                                    {...params}
                                                    error={values.checkedMuni && isError && !values.finishDateMuni}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3} xl={2} sx={{ paddingLeft: "5px !important" }}>
                                        <Checkbox
                                            name="checkedOthers"
                                            checked={values.checkedOthers}
                                            onChange={(value) => handleChangeCheck(value, 10)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={!values.initDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={9} xl={10}>
                                        <TextField
                                            fullWidth
                                            disabled={!values.checkedOthers}
                                            label={t("services.others")}
                                            name="nameOthers"
                                            onChange={handleChange}
                                            value={values.nameOthers}
                                            variant="outlined"
                                            error={values.checkedOthers && isError && !values.nameOthers}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={!values.checkedOthers}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothOthers"
                                    onChange={handleChange}
                                    value={values.tothOthers}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedOthers && isError && values.tothOthers === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.startDateOthers}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.startDateOthers}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={!values.checkedOthers}
                                    value={values.finishDateOthers}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.finishDateOthers}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}></Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label={t("services.hoursTotal")}
                                    value={sumTotalAddHours}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRate">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRate"
                                        value={values.addHourFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRateAdd">
                                        {t("services.hourlyRateAdd")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRateAdd"
                                        value={values.addHourFee * sumTotal}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRateAdd")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
